import { Check, Building2, House } from 'lucide-react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemText,
  DropdownMenuTrigger,
  useTailwindBreakpoint,
} from '@eye-able/ui';
import { useAtom } from 'jotai';
import { selectedGuestOrgAtom } from '@/global-store';
import { useFetchGuestOrganisations } from '@/http/queries/useFetchGuestOrganisations';
import resetGuestOrgSelections from '@helpers/resetGuestOrgSelections';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import type { BrandButtonProps } from '@/branding/useOrgBranding';

const GuestOrgSelect = ({ brandProps }: { brandProps?: BrandButtonProps }) => {
  const { t } = useInlineTranslation();
  const isDesktop = useTailwindBreakpoint('xl');
  const [selectedGuestOrg, setSelectedGuestOrg] = useAtom(selectedGuestOrgAtom);
  const { data: guestOrganisations } = useFetchGuestOrganisations();

  // No guest memberships -> nothing to switch to, hide the control entirely.
  if (!guestOrganisations || guestOrganisations.length === 0) return null;

  const ownLabel = t('myOrg');
  const activeOrg = guestOrganisations.find(
    (org) => org.organisationId === selectedGuestOrg,
  );
  const triggerLabel = activeOrg
    ? (activeOrg.name ?? `#${activeOrg.organisationId}`)
    : ownLabel;
  const TriggerIcon = activeOrg ? Building2 : House;

  // At switch, we reset the selected guest org localstorage settings and reload the homepage to ensure all state is cleared.
  const select = (organisationId: number | null) => {
    if (organisationId === selectedGuestOrg) return;
    resetGuestOrgSelections();
    setSelectedGuestOrg(organisationId);
    window.location.assign('/');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type='button'
          variant='ghost'
          size={isDesktop ? 'default' : 'icon'}
          aria-label={t('organisation')}
          {...brandProps}
        >
          <TriggerIcon size={isDesktop ? 20 : 18} aria-hidden />
          <span className='eye:max-xl:sr-only'>{triggerLabel}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align='end'
        side='bottom'
        aria-label={t('organisation')}
      >
        <DropdownMenuItem
          role='menuitemradio'
          aria-checked={selectedGuestOrg == null}
          onClick={() => select(null)}
        >
          <House size={16} aria-hidden />
          <DropdownMenuItemText>{ownLabel}</DropdownMenuItemText>
          <span
            aria-hidden
            className='eye:flex eye:h-lh eye:w-16 eye:items-center eye:justify-end'
          >
            {selectedGuestOrg == null ? <Check size={16} /> : null}
          </span>
        </DropdownMenuItem>

        {guestOrganisations.map((org) => (
          <DropdownMenuItem
            key={org.organisationId}
            role='menuitemradio'
            aria-checked={org.organisationId === selectedGuestOrg}
            onClick={() => select(org.organisationId)}
          >
            <Building2 size={16} aria-hidden />
            <DropdownMenuItemText>
              {org.name ?? `#${org.organisationId}`}
            </DropdownMenuItemText>
            <span
              aria-hidden
              className='eye:flex eye:h-lh eye:w-16 eye:items-center eye:justify-end'
            >
              {org.organisationId === selectedGuestOrg ? (
                <Check size={16} />
              ) : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default GuestOrgSelect;
